import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export interface WeightRecord {
  id: string;
  pet_id: string;
  user_id: string;
  weight_kg: number;
  measurement_date: string;
  measurement_context: string | null;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export function useWeightRecords(petId: string | undefined) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: records, isLoading } = useQuery({
    queryKey: ['pet_weight_records', petId],
    queryFn: async () => {
      if (!petId) return [];
      const { data, error } = await supabase
        .from('pet_weight_records')
        .select('*')
        .eq('pet_id', petId)
        .order('measurement_date', { ascending: false });
      if (error) throw error;
      return (data || []) as WeightRecord[];
    },
    enabled: !!petId,
  });

  const latestWeight = records?.[0] ?? null;

  const addRecord = useMutation({
    mutationFn: async ({ weight_kg, measurement_date, notes }: { weight_kg: number; measurement_date: string; notes?: string }) => {
      if (!petId || !user?.id) throw new Error('Missing petId or user');
      const { data, error } = await supabase
        .from('pet_weight_records')
        .insert({
          pet_id: petId,
          user_id: user.id,
          weight_kg,
          measurement_date,
          notes: notes || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pet_weight_records', petId] });
    },
  });

  const updateRecord = useMutation({
    mutationFn: async ({ id, weight_kg, measurement_date }: { id: string; weight_kg: number; measurement_date: string }) => {
      const { data, error } = await supabase
        .from('pet_weight_records')
        .update({ weight_kg, measurement_date })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pet_weight_records', petId] });
    },
  });

  const deleteRecord = useMutation({
    mutationFn: async (recordId: string) => {
      const { error } = await supabase
        .from('pet_weight_records')
        .delete()
        .eq('id', recordId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pet_weight_records', petId] });
    },
  });

  return { records: records || [], latestWeight, isLoading, addRecord, updateRecord, deleteRecord };
}
